import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle, RefreshCw, Save, Shield } from "lucide-react";

const TOKEN_KEY = "ct_login_token";

const formatIndianTime = value => {
  if (!value) return "";
  const text = String(value).trim();
  const normalized = /^\d{4}-\d{2}-\d{2}T/.test(text) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(text)
    ? `${text}+05:30`
    : text;
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return text;

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date);
};

const expiryFromJwt = token => {
  try {
    const encoded = String(token || "").split(".")[1] || "";
    const normalized = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), "=");
    const payload = JSON.parse(atob(padded));
    return payload?.exp ? new Date(Number(payload.exp) * 1000).toISOString() : "";
  } catch {
    return "";
  }
};

export default function DhanTokenManager() {
  const token = localStorage.getItem(TOKEN_KEY);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [pin, setPin] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [status, setStatus] = useState("");
  const [verifyStatus, setVerifyStatus] = useState("");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/login/data/accounts", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || "Account load failed");

      const dhanAccounts = (data.accounts || [])
        .filter(acc => String(acc.broker || "").toLowerCase() === "dhan")
        .map(acc => ({
          ...acc,
          ownerUserId: String(acc.ownerUserId || acc.ownerUsername || "admin"),
          ownerUsername: acc.ownerUsername || acc.ownerUserId || "admin",
        }));

      setAccounts(dhanAccounts);
      const firstUserId = dhanAccounts[0]?.ownerUserId || "";
      setSelectedUserId(current => current || firstUserId);
      setSelectedAccountId(current => current || dhanAccounts[0]?.id || "");
    } catch (nextError) {
      setError(nextError.message || "Account load failed");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadAccounts();
  }, [token, loadAccounts]);

  const userOptions = useMemo(() => {
    const map = new Map();
    accounts.forEach(acc => {
      map.set(String(acc.ownerUserId), acc.ownerUsername || acc.ownerUserId);
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [accounts]);

  const visibleAccounts = useMemo(
    () => accounts.filter(acc => String(acc.ownerUserId) === String(selectedUserId)),
    [accounts, selectedUserId]
  );

  const selectedAccount = useMemo(
    () => accounts.find(acc =>
      String(acc.ownerUserId) === String(selectedUserId) &&
      String(acc.id) === String(selectedAccountId)
    ),
    [accounts, selectedUserId, selectedAccountId]
  );

  useEffect(() => {
    if (!visibleAccounts.some(acc => String(acc.id) === String(selectedAccountId))) {
      setSelectedAccountId(visibleAccounts[0]?.id || "");
    }
  }, [visibleAccounts, selectedAccountId]);

  useEffect(() => {
    setPin(selectedAccount?.dhanPin || "");
    setVerifyStatus("");
  }, [selectedAccount]);

  const accountsWithCurrentPin = useCallback((nextAccounts = accounts) => {
    if (!selectedAccount) return nextAccounts;

    return nextAccounts.map(acc =>
      String(acc.ownerUserId) === String(selectedUserId) &&
      String(acc.id) === String(selectedAccount.id)
        ? {
            ...acc,
            dhanPin: pin.trim(),
          }
        : acc
    );
  }, [accounts, pin, selectedAccount, selectedUserId]);

  const saveAccounts = async (nextAccounts = accounts) => {
    const accountsToSave = accountsWithCurrentPin(nextAccounts);

    setSaving(true);
    setError("");
    setStatus("");
    try {
      const res = await fetch("/api/login/data/accounts", {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ accounts: accountsToSave }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || "Save failed");
      setStatus("Accounts saved. The new access token is now active in Accounts page.");
      setAccounts(accountsToSave);
      localStorage.setItem("ct_accounts", JSON.stringify(accountsToSave));
      window.dispatchEvent(new Event("accountsUpdated"));
    } catch (nextError) {
      setError(nextError.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generateAndSaveToken = async () => {
    if (!selectedAccount) {
      setError("Select a Dhan account first");
      return;
    }
    if (!pin.trim()) {
      setError("Dhan PIN is required");
      return;
    }
    if (!totpSecret.trim() && !totpCode.trim()) {
      setError("Enter TOTP secret or current TOTP code");
      return;
    }

    setLoading(true);
    setError("");
    setStatus("");
    try {
      const res = await fetch("/api/dhan-token/generate", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          clientId: selectedAccount.id,
          pin: pin.trim(),
          totpSecret: totpSecret.trim(),
          totpCode: totpCode.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || "Token generation failed");

      const nextAccounts = accounts.map(acc =>
        String(acc.ownerUserId) === String(selectedUserId) &&
        String(acc.id) === String(selectedAccount.id)
          ? {
              ...acc,
              accessToken: data.accessToken,
              dhanPin: pin.trim(),
              tokenUpdatedAt: new Date().toISOString(),
              tokenExpiresAt: data.expiryTime || data.expiresAt || expiryFromJwt(data.accessToken),
              status: "Disconnected",
            }
          : acc
      );

      setAccounts(nextAccounts);
      await saveAccounts(nextAccounts);
      setStatus(`New access token generated and saved for ${selectedAccount.id}.`);
      setVerifyStatus("");
      setTotpCode("");
    } catch (nextError) {
      setError(nextError.message || "Token generation failed");
    } finally {
      setLoading(false);
    }
  };

  const verifyToken = async () => {
    if (!selectedAccount?.accessToken) {
      setError("No saved access token found for this Dhan account");
      return;
    }

    setLoading(true);
    setError("");
    setVerifyStatus("");
    try {
      const res = await fetch("/api/dhan/profile", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ accessToken: selectedAccount.accessToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Token verification failed");

      const raw = data.raw || {};
      const clientId =
        raw.dhanClientId ||
        raw.clientId ||
        raw.dhanClientUcc ||
        selectedAccount.id;
      const name =
        data.name ||
        raw.dhanClientName ||
        raw.clientName ||
        raw.name ||
        "";

      setVerifyStatus(`Valid token | Client ID: ${clientId}${name ? ` | ${name}` : ""}`);
    } catch (nextError) {
      setError(nextError.message || "Token verification failed");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "p-3 rounded bg-gray-700 border border-gray-600 w-full";

  return (
    <div className="bg-[#1e293b] rounded-xl p-5 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield size={18} /> Dhan Access Token Manager
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Generate a fresh Dhan access token and replace it in the selected Accounts page entry.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setIsSettingsOpen(open => !open)}
            className="bg-gray-700 hover:bg-gray-600 rounded px-4 py-2 font-semibold"
          >
            {isSettingsOpen ? "Close Settings" : "Open Settings"}
          </button>

          <button
            type="button"
            onClick={loadAccounts}
            disabled={loading}
            className="btn-solid-green rounded px-4 py-2 font-semibold flex items-center gap-2"
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      {isSettingsOpen && (
  <>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold mb-2">User ID</label>
          <select
            value={selectedUserId}
            onChange={event => setSelectedUserId(event.target.value)}
            className={inputClass}
          >
            {userOptions.map(user => (
              <option key={user.id} value={user.id}>
                {user.label} ({user.id})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">Dhan Client ID</label>
          <select
            value={selectedAccountId}
            onChange={event => setSelectedAccountId(event.target.value)}
            className={inputClass}
          >
            {visibleAccounts.map(acc => (
              <option key={`${acc.ownerUserId}-${acc.id}`} value={acc.id}>
                {acc.name || acc.id} ({acc.id})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">Dhan PIN</label>
          <input
            type="password"
            value={pin}
            onChange={event => setPin(event.target.value)}
            className={inputClass}
            placeholder={selectedAccount?.dhanPin ? "Saved PIN loaded" : "Enter Dhan login PIN"}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">TOTP Secret</label>
          <input
            type="password"
            value={totpSecret}
            onChange={event => setTotpSecret(event.target.value)}
            className={inputClass}
            placeholder="Optional if entering current TOTP"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">Current TOTP Code</label>
          <input
            value={totpCode}
            onChange={event => setTotpCode(event.target.value)}
            className={inputClass}
            placeholder="6-digit code, optional if secret is entered"
            inputMode="numeric"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">Selected Token Status</label>
          <div className="p-3 rounded bg-gray-800 border border-gray-700 text-sm text-gray-300 min-h-[48px]">
            {selectedAccount?.accessToken ? (
              <div className="space-y-1">
                <div className="text-emerald-300 font-semibold">Access token saved</div>
                <div>Generated: {formatIndianTime(selectedAccount.tokenUpdatedAt) || "not tracked"} IST</div>
                <div>Valid till: {formatIndianTime(selectedAccount.tokenExpiresAt || expiryFromJwt(selectedAccount.accessToken)) || "not tracked"} IST</div>
              </div>
            ) : (
              "No token saved for this account"
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={generateAndSaveToken}
          disabled={loading || saving || !selectedAccount}
          className="btn-solid-green rounded px-4 py-3 font-semibold flex items-center gap-2"
        >
          <Shield size={16} /> Generate Token & Save
        </button>

        <button
          type="button"
          onClick={() => saveAccounts()}
          disabled={saving}
          className="bg-blue-500 hover:bg-blue-400 rounded px-4 py-3 font-semibold flex items-center gap-2"
        >
          <Save size={16} /> Save Accounts
        </button>

        <button
          type="button"
          onClick={verifyToken}
          disabled={loading || saving || !selectedAccount?.accessToken}
          className="bg-emerald-600 hover:bg-emerald-500 rounded px-4 py-3 font-semibold flex items-center gap-2"
        >
          <CheckCircle size={16} /> Verify Token
        </button>
      </div>

      {status && <div className="mt-4 text-sm text-green-300">{status}</div>}
      {verifyStatus && <div className="mt-4 text-sm text-emerald-300">{verifyStatus}</div>}
      {error && <div className="mt-4 text-sm text-red-300">{error}</div>}

        </>
)}

      <div className="mt-5 text-xs text-gray-400 leading-6">
        The new Dhan token will revoke/replace the old token for that Dhan account. Avoid running this during active copy trading unless the websocket/token reconnect flow is handled.
      </div>
    </div>
  );
}
